import { IFocusUtilsService } from "../models/focus-utils";
import { IDomUtilsService } from "../models/dom-utils";
import { IWidgetUtilsService } from "../models";

// TAB key code - will be moved to utils service in future refactoring
const TAB_KEY = 9;

/**
 * Focus Utils Service - provides focus-related utilities
 
 */
export class FocusUtilsService implements IFocusUtilsService {
    constructor(
        private $: JQueryStatic,
        private domUtils: IDomUtilsService,
        private widgetUtilsService: IWidgetUtilsService
    ) {}

    /**
     * Check if an element is focusable
     */
    focusable(element: HTMLElement, isTabIndexNotNaN: boolean): boolean {
        const nodeName = element.nodeName.toLowerCase();

        return (/input|select|textarea|button|object/.test(nodeName) ?
                !(element as HTMLInputElement).disabled :
                nodeName === "a" ?
                (element as HTMLAnchorElement).href || isTabIndexNotNaN :
                isTabIndexNotNaN
               ) &&
            this.visible(element);
    }

    /**
     * Check if an element is visible (not hidden or with visibility:hidden)
     */
    visible(element: HTMLElement): boolean {
        const $ = this.$;
        return ($.expr.pseudos as any).visible(element) &&
            !$(element).parents().addBack().filter(function(this: HTMLElement) {
                return $.css(this, "visibility") === "hidden";
            }).length;
    }

    /**
     * Check if an element is kendo-focusable (focusable with positive tabindex)
     */
    kendoFocusable(element: HTMLElement): boolean {
        const idx = this.$(element).attr("tabindex");
        const numIdx = idx !== undefined ? parseInt(idx, 10) : NaN;
        return this.focusable(element, !isNaN(numIdx) && numIdx > -1);
    }

    /**
     * Focus an element while preserving scroll positions of parent containers
     */
    focusElement(element: JQuery): void {
        const $ = this.$;
        const domUtils = this.domUtils;
        const scrollTopPositions: number[] = [];
        const scrollableParents = element.parentsUntil("body")
                .filter(function(this: HTMLElement, index: number, el: HTMLElement) {
                    const computedStyle = domUtils.getComputedStyles(el, ["overflow"]);
                    return computedStyle.overflow !== "visible";
                })
                .add(window as any);

        scrollableParents.each(function(this: HTMLElement | Window, index: number, parent: HTMLElement | Window) {
            scrollTopPositions[index] = $(parent as any).scrollTop() || 0;
        });

        try {
            //The setActive method does not cause the document to scroll to the active object in the current page
            (element[0] as any).setActive();
        } catch (e) {
            element.trigger("focus");
        }

        scrollableParents.each(function(this: HTMLElement | Window, index: number, parent: HTMLElement | Window) {
            $(parent as any).scrollTop(scrollTopPositions[index]);
        });
    }

    /**
     * Focus the next focusable element in the document
     */
    focusNextElement(): void {
        const $ = this.$;
        if (document.activeElement) {
            const focussable = $(":kendoFocusable");
            const index = focussable.index(document.activeElement);

            if (index > -1) {
               const nextElement = focussable[index + 1] || focussable[0];
               (nextElement as HTMLElement).focus();
            }
        }
    }

    /**
     * Set up form cycling - when tabbing from last element, cycle to first
     */
    cycleForm(form: JQuery): void {
        const $ = this.$;
        const self = this;
        const firstElement = form.find("input, .k-widget, .k-dropdownlist, .k-combobox").first();
        const lastElement = form.find("button, .k-button").last();

        function focus(el: JQuery) {
            const widget = self.widgetUtilsService.widgetInstance(el);

            if (widget && widget.focus) {
              widget.focus();
            } else {
              el.trigger("focus");
            }
        }

        lastElement.on("keydown", function(e: JQuery.KeyDownEvent) {
          if (e.keyCode === TAB_KEY && !e.shiftKey) {
            e.preventDefault();
            focus(firstElement);
          }
        });

        firstElement.on("keydown", function(e: JQuery.KeyDownEvent) {
          if (e.keyCode === TAB_KEY && e.shiftKey) {
            e.preventDefault();
            focus(lastElement);
          }
        });
    }

    /**
     * Get the focusable element for a widget
     */
    getWidgetFocusableElement(element: JQuery): JQuery {
        const $ = this.$;
        const nextFocusable = element.closest(":kendoFocusable");
        const widgetInstance = this.widgetUtilsService.widgetInstance(element);
        let target: JQuery;

        if (nextFocusable.length) {
            target = nextFocusable;
        } else if (widgetInstance) {
            target = widgetInstance.options.name === 'Editor' ?
                $(widgetInstance.body) :
                widgetInstance.wrapper.find(":kendoFocusable").first();
        } else {
            target = element;
        }

        return target;
    }

    /**
     * Register the :kendoFocusable pseudo-selector with jQuery
     */
    registerFocusableSelector(): void {
        const self = this;
        const $ = this.$;
        $.extend($.expr.pseudos, {
            kendoFocusable: function(element: HTMLElement) {
                return self.kendoFocusable(element);
            }
        });
    }

    /**
     * Get the currently focused element in the document.
     * Handles exceptions that can occur in some browsers.
     * @returns The active element
     */
    activeElement(): Element {
        try {
            return document.activeElement!;
        } catch (e) {
            return (document.documentElement as any).activeElement;
        }
    }
}
